/**
 * The sidebar's destinations and type rows (1.I-1 deliverable 8), as pure rules: a row shows
 * only when its owner's route is in the router's route tree and its flag, if any, is on, so
 * each row appears when its route merges, with no edit here.
 */
import type { DocTypeV1, Flags } from '@codex/contracts';

export interface Destination {
  readonly key: 'types' | 'chronicle' | 'secrets' | 'trash' | 'settings';
  readonly label: string;
  readonly path: string;
  readonly flag?: keyof Flags;
}

export const DESTINATIONS: readonly Destination[] = [
  { key: 'types', label: 'Manage types', path: '/types' },
  { key: 'chronicle', label: 'Chronicle', path: '/chronicle', flag: 'chrono' },
  { key: 'secrets', label: 'Secrets', path: '/secrets', flag: 'ledger' },
  { key: 'trash', label: 'Trash', path: '/trash' },
  { key: 'settings', label: 'Settings', path: '/settings' },
];

export function visibleDestinations(
  routePaths: readonly string[],
  flags: Flags,
): readonly Destination[] {
  return DESTINATIONS.filter(
    (row) => routePaths.includes(row.path) && (row.flag === undefined || flags[row.flag] === true),
  );
}

export const CATALOGUE_ROUTE = '/catalogue/$typeKey';
export const INBOX_ROUTE = '/inbox';

/** The catalogue route of a type, or null when the route or the `catalogue` flag is missing. */
export function typeRowHref(
  typeKey: string,
  routePaths: readonly string[],
  flags: Flags,
): string | null {
  if (!routePaths.includes(CATALOGUE_ROUTE) || flags.catalogue !== true) return null;
  return `/catalogue/${encodeURIComponent(typeKey)}`;
}

/** The Inbox row shows only with its route and the `catalogue` flag. */
export function inboxVisible(routePaths: readonly string[], flags: Flags): boolean {
  return routePaths.includes(INBOX_ROUTE) && flags.catalogue === true;
}

/** The canon, unarchived types, in the order `types.list` answered them. */
export function canonTypeRows(types: readonly DocTypeV1[]): readonly DocTypeV1[] {
  return types.filter((type) => type.library === 'canon' && !type.archived);
}

export const REFERENCE_ROUTE = '/reference';

/** The reference-library types, unarchived, in the order `types.list` answered them. */
export function referenceTypeRows(types: readonly DocTypeV1[]): readonly DocTypeV1[] {
  return types.filter((type) => type.library === 'reference' && !type.archived);
}

/** The Reference section shows with the `reference` flag; its rows link when `/reference` exists. */
export function referenceHref(routePaths: readonly string[]): string | null {
  return routePaths.includes(REFERENCE_ROUTE) ? REFERENCE_ROUTE : null;
}
